/**
 * ReportPage - PDD 報告頁面
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useProject } from '../contexts/ProjectContext';
import { getCompleteness, getHtmlReport, downloadPdfReport, CompletenessData } from '../services/reportService';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { usePermission } from '../hooks/usePermission';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const ReportPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { selectedProjectId, selectedProject } = useProject();
  const { hasPermission } = usePermission();
  const toast = useToast();
  const canPrint = hasPermission('pdd_report', 'print');
  const canFile  = hasPermission('pdd_report', 'file');
  const [data, setData] = useState<CompletenessData | null>(null);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCompleteness = useCallback(async (projectId: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getCompleteness(projectId);
      setData(result);
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('report.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (selectedProjectId) {
      loadCompleteness(selectedProjectId);
    } else {
      setData(null);
    }
  }, [selectedProjectId, loadCompleteness]);

  const handlePrint = async () => {
    if (!selectedProjectId) return;
    setPrinting(true);
    try {
      const html = await getHtmlReport(selectedProjectId);
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(html);
        win.document.close();
      }
    } catch (e: any) {
      toast.error(t('report.printFailed') + (e?.response?.data?.detail || e.message));
    } finally {
      setPrinting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!selectedProjectId) return;
    setExporting(true);
    try {
      const title = data?.project_title || `Project_${selectedProjectId}`;
      const filename = `PDD_${title.replace(/[^a-zA-Z0-9\u4e00-\u9fff]/g, '_')}.pdf`;
      await downloadPdfReport(selectedProjectId, filename);
    } catch (e: any) {
      toast.error(t('report.pdfFailed') + (e?.response?.data?.detail || e.message));
    } finally {
      setExporting(false);
    }
  };

  const getStatusColor = (pct: number, hasData: boolean) => {
    if (!hasData) return '#9e9e9e';
    if (pct >= 80) return '#43a047';
    if (pct >= 40) return '#fb8c00';
    return '#e53935';
  };

  const getStatusLabel = (pct: number, hasData: boolean) => {
    if (!hasData) return t('report.statusEmpty');
    if (pct >= 80) return t('report.statusDone');
    if (pct >= 40) return t('report.statusInProgress');
    return t('report.statusIncomplete');
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <FunctionPageHeader
          funcCode="pdd_report"
          subtitle={selectedProject?.project_title}
        />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canPrint && (
            <button
              className="btn-primary"
              onClick={handlePrint}
              disabled={printing || loading || !data}
            >
              {printing ? t('report.printing') : `🖨️ ${t('report.print')}`}
            </button>
          )}
          {canFile && (
            <button
              className="btn-primary"
              onClick={handleExportPdf}
              disabled={exporting || loading || !data}
              style={{ background: exporting ? '#90a4ae' : '#388e3c' }}
            >
              {exporting ? t('report.exporting') : `📄 ${t('report.exportPdf')}`}
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      {!selectedProjectId && !loading && (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: 16 }}>
          {t('report.selectProject')}
        </div>
      )}

      {loading && (
        <div className="loading">{t('report.loading')}</div>
      )}

      {data && !loading && (
        <div>
          {/* 整體完成度 */}
          <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 6, padding: '16px 20px', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: '#333' }}>{t('report.overallProgress')}</span>
              <span style={{ fontSize: 22, fontWeight: 700, color: getStatusColor(data.overall_percentage, true) }}>
                {data.overall_percentage}%
              </span>
            </div>
            <div style={{ height: 10, background: '#e0e0e0', borderRadius: 5, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${data.overall_percentage}%`,
                background: getStatusColor(data.overall_percentage, true),
                borderRadius: 5,
                transition: 'width 0.5s ease',
              }} />
            </div>
          </div>

          {/* Section 清單 */}
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 100 }}>{t('report.colSection')}</th>
                <th>{t('report.colName')}</th>
                <th style={{ width: 100, textAlign: 'center' }}>{t('report.colStatus')}</th>
                <th style={{ width: 200, textAlign: 'right' }}>{t('report.colProgress')}</th>
              </tr>
            </thead>
            <tbody>
              {data.sections.map((sec) => {
                const color = getStatusColor(sec.percentage, sec.has_data);
                return (
                  <tr key={sec.key}>
                    <td style={{ fontWeight: 600, color: '#666', whiteSpace: 'nowrap' }}>
                      {sec.key.toUpperCase().replace('_', '.')}
                    </td>
                    <td>{i18n.language === 'zh-TW' ? sec.label_zh : sec.label_en}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 10px',
                        background: color + '20',
                        color,
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 600,
                        border: `1px solid ${color}40`,
                      }}>
                        {getStatusLabel(sec.percentage, sec.has_data)}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
                        <div style={{ flex: 1, height: 8, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden', maxWidth: 120 }}>
                          <div style={{ height: '100%', width: `${sec.percentage}%`, background: color, borderRadius: 4 }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color, minWidth: 36, textAlign: 'right' }}>
                          {sec.percentage}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReportPage;
